// Three-tier dish image cache with validation
// Tier 1: In-memory Map (instant, session-only)
// Tier 2: IndexedDB (fast, persistent, on-device)
// Tier 3: Supabase Storage (shared across users)
import { fmtErr } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { checkDishImage, isVerdict } from "@/lib/foodCheck";

const DB_NAME = "yumz-images-v2";
const STORE_NAME = "images";
const META_STORE = "meta";

// ── Concurrency tuning (Wave B.1) ────────────────────────────────────
// iOS WKWebView caps concurrent fetches at ~6 per origin; the previous
// MAX_CONCURRENT = 20 overscheduled, with the extra requests sitting
// queued at the network layer rather than running in parallel. Worse,
// at the time the queue overlapped with edge-function calls (smart
// prompts, store-dish-image) and competed for the same slot pool.
// Lower numbers tuned to platform: iOS = 4, web = 8.
function detectIsCapacitorNative(): boolean {
  if (typeof window === "undefined") return false;
  // Cheap detection — we don't want to import @capacitor/core just for this.
  // Capacitor exposes `Capacitor.isNativePlatform()` via the global at runtime
  // when running inside the native shell.
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } };
  if (w.Capacitor?.isNativePlatform?.()) return true;
  const platform = w.Capacitor?.getPlatform?.();
  return platform === "ios" || platform === "android";
}
const IMAGE_GEN_CONCURRENCY = detectIsCapacitorNative() ? 4 : 8;

// ── Stats tracking ───────────────────────────────────────────────
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const GOOGLE_AI_KEY = import.meta.env.VITE_GOOGLE_AI_KEY as string | undefined;

const pendingStats = { generated: 0, db_hits: 0, prefetched: 0, total_requests: 0 };
let statsFlushTimer: ReturnType<typeof setTimeout> | null = null;

function trackStat(type: "generated" | "db_hits" | "prefetched") {
  pendingStats[type]++;
  pendingStats.total_requests++;
  // Flush every 10 seconds to avoid hammering DB
  if (!statsFlushTimer) {
    statsFlushTimer = setTimeout(flushStats, 10000);
  }
}

async function flushStats() {
  statsFlushTimer = null;
  const { generated, db_hits, prefetched, total_requests } = pendingStats;
  if (total_requests === 0) return;
  // Reset before async call
  pendingStats.generated = 0;
  pendingStats.db_hits = 0;
  pendingStats.prefetched = 0;
  pendingStats.total_requests = 0;
  try {
    const today = new Date().toISOString().slice(0, 10);
    await fetch(`${SUPABASE_URL}/rest/v1/rpc/increment_image_stats`, {
      method: "POST",
      headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}`, "Content-Type": "application/json" },
      body: JSON.stringify({ p_date: today, p_generated: generated, p_db_hits: db_hits, p_prefetched: prefetched, p_total: total_requests }),
    }).catch(() => {});
  } catch {}
}

// ── Tier 1: In-memory cache ──────────────────────────────────────
const memCache = new Map<string, string>();

// ── Key helpers ──────────────────────────────────────────────────
function normalizeKey(name: string): string {
  return (name || "").toLowerCase().trim().replace(/\s+/g, " ");
}

export function canonicalDishName(name: string): string {
  const cleaned = normalizeKey(name)
    .replace(/œ/g, "oe")
    .replace(/[éèêë]/g, "e")
    .replace(/[áàâäå]/g, "a")
    .replace(/[íìîï]/g, "i")
    .replace(/[óòôöø]/g, "o")
    .replace(/[úùûü]/g, "u")
    .replace(/[ç]/g, "c")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\b(grilled|grillad|roasted|seared|fried|crispy|homemade|house|signature|classic|fresh)\b/g, "")
    .replace(/\b(with|served|and|the|a|an|of|in|on)\b/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  // Sort words so "pizza margarita" and "margarita pizza" produce the same key
  return cleaned.split(" ").filter(Boolean).sort().join(" ");
}

/**
 * Wave L4 — restaurant slug. Strips common descriptor words so
 * "Bullfish", "Bullfish Steak & Seafood", "Bullfish Grill" all
 * canonicalize to "bullfish". Mirrors the server-side restaurantSlug
 * in the edge functions (get-dish-images, get-dish-image-one,
 * store-dish-image). Same algorithm both sides — small drift here
 * would re-introduce the split-cache problem L4 is trying to fix.
 */
export function canonicalRestaurantSlug(name: string | null | undefined): string {
  return ((name || "unknown").toLowerCase().trim()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(
      /\b(steak|steakhouse|seafood|restaurant|grill|bistro|cafe|bar|kitchen|diner|house|pub|tavern|eatery|the|and|of|by)\b/g,
      "",
    )
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/^-|-$/g, "")) || "unknown";
}

function meaningfulDescriptionKey(description?: string): string {
  const text = canonicalDishName(description || "")
    .split(/\s+/)
    .filter((t) => t.length > 3)
    .filter((t) => !/^(sauce|served|side|fresh|house|classic|plate|bowl|with)$/.test(t))
    .slice(0, 4)
    .join(" ");
  return text;
}

function dishReuseKey(dishName: string, description?: string): string {
  const dish = canonicalDishName(dishName);
  const desc = meaningfulDescriptionKey(description);
  // Dish-first: only split cache identity when the dish name is too generic and description materially identifies it.
  if (dish.length < 5 && desc) return `${dish} ${desc}`.trim();
  return dish;
}

function makeKey(dishName: string, _restaurantName?: string, description?: string): string {
  // Product decision: cache reuse is dish-first, not menu/restaurant-first.
  return dishReuseKey(dishName, description);
}

function tokenSet(name: string): Set<string> {
  return new Set(canonicalDishName(name).split(/[^a-z0-9]+/).filter((t) => t.length > 2));
}

function similarity(a: string, b: string): number {
  const A = tokenSet(a);
  const B = tokenSet(b);
  if (!A.size || !B.size) return 0;
  let overlap = 0;
  for (const t of A) if (B.has(t)) overlap++;
  return overlap / Math.max(A.size, B.size);
}

async function findSimilarCachedImage(dishName: string): Promise<string | null> {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAllKeys();
      req.onsuccess = async () => {
        const keys = (req.result || []).map(String).filter((k) => !k.startsWith("id:") && !k.startsWith("photo:"));
        let bestKey: string | null = null;
        let bestScore = 0;
        for (const key of keys) {
          const s = similarity(dishName, key);
          if (s > bestScore) { bestScore = s; bestKey = key; }
        }
        if (!bestKey || bestScore < 0.66) return resolve(null);
        const val = await idbGet(STORE_NAME, bestKey);
        if (val) memCache.set(makeKey(dishName), val);
        resolve(val || null);
      };
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

// ── IndexedDB ────────────────────────────────────────────────────
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) db.createObjectStore(STORE_NAME);
      if (!db.objectStoreNames.contains(META_STORE)) db.createObjectStore(META_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(store: string, key: string): Promise<any> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(store, "readonly");
      const s = tx.objectStore(store);
      const req = s.get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

async function idbPut(store: string, key: string, value: any): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(store, "readwrite");
    tx.objectStore(store).put(value, key);
  } catch { /* silent */ }
}

// ── Metadata ─────────────────────────────────────────────────────
interface ImageMeta {
  source: "ai" | "user";
  quality?: number;
  createdAt: string;
}

// ── Public: get cached image ─────────────────────────────────────
export async function getCachedImage(dishName: string, restaurantName?: string, description?: string, originalName?: string): Promise<string | null> {
  const key = makeKey(dishName, restaurantName, description);

  // Also try the original (untranslated) name as key — translation is inconsistent
  const origKey = originalName && originalName !== dishName ? makeKey(originalName) : null;

  // Tier 1
  if (memCache.has(key)) return memCache.get(key)!;
  if (origKey && memCache.has(origKey)) return memCache.get(origKey)!;

  // Tier 2
  const idb = await idbGet(STORE_NAME, key);
  if (idb) { memCache.set(key, idb); return idb; }
  if (origKey) {
    const idbOrig = await idbGet(STORE_NAME, origKey);
    if (idbOrig) { memCache.set(origKey, idbOrig); memCache.set(key, idbOrig); return idbOrig; }
  }

  const fuzzyLocal = await findSimilarCachedImage(dishName);
  if (fuzzyLocal) return fuzzyLocal;

  // Tier 3: Supabase Storage — try translated name first, then original
  const remote = await supabaseGet(dishName, restaurantName);
  if (remote) {
    memCache.set(key, remote);
    await idbPut(STORE_NAME, key, remote);
    if (origKey) { memCache.set(origKey, remote); await idbPut(STORE_NAME, origKey, remote); }
    return remote;
  }
  if (origKey) {
    const remoteOrig = await supabaseGet(originalName!, restaurantName);
    if (remoteOrig) {
      memCache.set(origKey, remoteOrig); memCache.set(key, remoteOrig);
      await idbPut(STORE_NAME, origKey, remoteOrig); await idbPut(STORE_NAME, key, remoteOrig);
      return remoteOrig;
    }
  }

  // Legacy: try old DB format
  try {
    const legacyKey = dishName.toLowerCase().trim();
    const legacyDb = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open("carte-lens-images", 1);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    const result = await new Promise<string | null>((resolve) => {
      try {
        const tx = legacyDb.transaction("images", "readonly");
        const req = tx.objectStore("images").get(legacyKey);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      } catch { resolve(null); }
    });
    if (result) {
      // Migrate to new DB
      memCache.set(key, result);
      await idbPut(STORE_NAME, key, result);
      return result;
    }
  } catch { /* no legacy DB */ }

  return null;
}

// ── Public: cache an image ───────────────────────────────────────
export async function cacheImage(
  dishName: string,
  imageUrl: string,
  restaurantName?: string,
  source: "ai" | "user" = "ai",
  quality?: number,
  description?: string,
  originalName?: string,
): Promise<void> {
  const key = makeKey(dishName, restaurantName, description);
  const origKey = originalName && originalName !== dishName ? makeKey(originalName) : null;

  // Check if existing user photo is better
  const meta: ImageMeta | null = await idbGet(META_STORE, key);
  if (meta && meta.source === "user" && (meta.quality || 0) >= 7 && source === "ai") {
    return; // don't replace good user photo with AI
  }

  // Store exactly the displayed image under the dish key.
  // Product decision: image identity follows the dish, not the restaurant.
  memCache.set(key, imageUrl);
  await idbPut(STORE_NAME, key, imageUrl);
  await idbPut(META_STORE, key, { source, quality, createdAt: new Date().toISOString() } as ImageMeta);

  // Also cache under the original (untranslated) name for cross-translation hits
  if (origKey) {
    memCache.set(origKey, imageUrl);
    await idbPut(STORE_NAME, origKey, imageUrl);
    await idbPut(META_STORE, origKey, { source, quality, createdAt: new Date().toISOString() } as ImageMeta);
  }

  // Upload only to the generic dish path so future restaurants reuse the same dish image.
  supabaseUpload(dishName, undefined, imageUrl).catch(() => {});
}

// ── Public: clear cached image (for flagging wrong images) ───────
export async function clearCachedImage(dishName: string, restaurantName?: string, description?: string, itemKey?: string): Promise<void> {
  const key = makeKey(dishName, restaurantName, description);
  const genericKey = makeKey(dishName);
  const exactKey = normalizeKey(dishName);
  memCache.delete(key);
  memCache.delete(genericKey);
  memCache.delete(exactKey);
  if (itemKey) memCache.delete(`item:${itemKey}`);
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    store.delete(key);
    store.delete(genericKey);
    store.delete(exactKey);
    if (itemKey) store.delete(`item:${itemKey}`);
  } catch { /* ignore */ }
}

// ── Public: get by dish ID ───────────────────────────────────────
export async function cacheImageById(dishId: string, imageUrl: string): Promise<void> {
  await idbPut(STORE_NAME, `id:${dishId}`, imageUrl);
  memCache.set(`id:${dishId}`, imageUrl);
}

export async function getCachedImageById(dishId: string): Promise<string | null> {
  const key = `id:${dishId}`;
  if (memCache.has(key)) return memCache.get(key)!;
  const val = await idbGet(STORE_NAME, key);
  if (val) memCache.set(key, val);
  return val;
}

export async function cacheImageByItemKey(itemKey: string, imageUrl: string): Promise<void> {
  await idbPut(STORE_NAME, `item:${itemKey}`, imageUrl);
  memCache.set(`item:${itemKey}`, imageUrl);
}

export async function getCachedImageByItemKey(itemKey: string): Promise<string | null> {
  const key = `item:${itemKey}`;
  if (memCache.has(key)) return memCache.get(key)!;
  const val = await idbGet(STORE_NAME, key);
  if (val) memCache.set(key, val);
  return val;
}

// ── Public: user photo storage ───────────────────────────────────
export async function saveUserPhoto(dishId: string, photoIndex: number, dataUrl: string): Promise<void> {
  await idbPut(STORE_NAME, `photo:${dishId}:${photoIndex}`, dataUrl);
}

export async function getUserPhotos(dishId: string, count: number): Promise<string[]> {
  const photos: string[] = [];
  for (let i = 0; i < count; i++) {
    const p = await idbGet(STORE_NAME, `photo:${dishId}:${i}`);
    if (p) photos.push(p);
  }
  return photos;
}

// ── Public: get image metadata ───────────────────────────────────
export async function getImageMeta(dishName: string, restaurantName?: string, description?: string): Promise<ImageMeta | null> {
  return idbGet(META_STORE, makeKey(dishName, restaurantName, description));
}

// ── Photo validation via Vision API ──────────────────────────────
export async function validateDishPhoto(
  photoDataUrl: string,
  expectedDishName: string
): Promise<{ isFood: boolean; matchesDish: boolean; quality: number }> {
  const fallback = { isFood: false, matchesDish: false, quality: 1 };
  try {
    const apiKey = import.meta.env.VITE_OPENROUTER_API_KEY;
    if (!apiKey) return fallback;

    const res = await fetch("https://openrouter.ai/api/v1/chat/completions", {
      method: "POST",
      headers: { "Authorization": `Bearer ${apiKey}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "google/gemini-2.5-flash-preview",
        messages: [{
          role: "user",
          content: [
            { type: "image_url", image_url: { url: photoDataUrl } },
            { type: "text", text: `This should be a photo of "${expectedDishName}". Reply JSON only: {"isFood":true/false,"matchesDish":true/false,"quality":1-10} Quality: 1=blurry/dark, 5=ok, 7=good, 10=pro.` },
          ],
        }],
        max_tokens: 80,
      }),
    });

    const data = await res.json();
    const text = data.choices?.[0]?.message?.content || "";
    const m = text.match(/\{[^}]+\}/);
    if (m) {
      const p = JSON.parse(m[0]);
      return { isFood: !!p.isFood, matchesDish: !!p.matchesDish, quality: Math.min(10, Math.max(1, parseInt(p.quality) || 5)) };
    }
    return fallback;
  } catch { return fallback; }
}


// ── Tier 3: Supabase Storage (shared across users) ──────────────
const BUCKET = "dish-images";

function storageKey(dishName: string, restaurantName?: string): string {
  const dish = normalizeKey(dishName).replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
  const rest = restaurantName ? normalizeKey(restaurantName).replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") : "generic";
  return `${rest}/${dish}.webp`;
}

function publicUrl(key: string): string {
  return `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${key}`;
}

async function supabaseGet(dishName: string, _restaurantName?: string): Promise<string | null> {
  if (!SUPABASE_URL) return null;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2000);
    const genericUrl = publicUrl(storageKey(dishName));
    const res = await fetch(genericUrl, { method: "HEAD", signal: controller.signal });
    clearTimeout(timeout);
    if (res.ok) return genericUrl;
    return null;
  } catch { return null; }
}

async function supabaseUpload(_dishName: string, _restaurantName: string | undefined, _dataUrl: string): Promise<string | null> {
  // Client-safe architecture: uploads/listing should go through a privileged Edge Function,
  // not a service key in the mobile app env.
  return null;
}


// Fast local-only cache check (skips Supabase network call)
export async function getCachedImageLocal(dishName: string, restaurantName?: string, description?: string): Promise<string | null> {
  const key = makeKey(dishName, restaurantName, description);
  if (memCache.has(key)) return memCache.get(key)!;
  const idb = await idbGet(STORE_NAME, key);
  if (idb) { memCache.set(key, idb); return idb; }
  const fuzzyLocal = await findSimilarCachedImage(dishName);
  if (fuzzyLocal) return fuzzyLocal;
  return null;
}

/** Compress a base64 image to ~400px JPEG and store in Supabase dish_images */
export async function compressAndStoreInDB(
  dishName: string,
  restaurantName?: string,
  description?: string,
  dataUrl?: string | null,
): Promise<void> {
  if (!dataUrl) return;
  try {
    // ── Wave E2: write-time Vision gate ─────────────────────────────────
    // Before we cache this image for OTHER users to see, ask Gemini Vision
    // whether the image is actually food AND matches the dish name. Bad
    // images get dropped on the floor here — the user who just generated
    // them still sees their own copy in memory; only the DB cache stays
    // clean. Fail-open on transient errors (rate limit / network) so we
    // don't block storage on Vision flakiness.
    //
    // This runs inside the already-fire-and-forget compressAndStoreInDB,
    // so it adds ~500ms–1s of background work but zero user-perceived
    // latency.
    // Wave L3 — flag-not-drop. The original gate would silently discard
    // the image when Vision said it was wrong, leaving an empty cache
    // slot. On re-scan we'd then regenerate the same problematic image
    // and Vision would reject it again, in an infinite cycle. Now we
    // STORE the image with flagged=true so it's still available to the
    // user who just generated it (their copy is in memory anyway) AND
    // shows up in the admin review queue. Clients filter flagged out
    // via the read paths, so the bad image never reaches another user.
    let writeAsFlagged = false;
    let verdictReason: string | undefined;
    if (GOOGLE_AI_KEY) {
      const verdict = await checkDishImage(dishName, description ?? null, dataUrl, {
        apiKey: GOOGLE_AI_KEY,
      });
      if (isVerdict(verdict)) {
        const isBad = !verdict.is_food || !verdict.matches_dish || verdict.quality === "bad";
        if (isBad) {
          writeAsFlagged = true;
          verdictReason = verdict.reason;
          console.warn(
            `[imageCache] write-time GATE flag (still storing) for "${dishName}": ${verdict.reason}`,
          );
        }
      }
      // On error (transient or schema-mismatch) fall through and store unflagged.
    }

    const img = new Image();
    const loaded = new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
    });
    img.src = dataUrl;
    await loaded;

    const MAX = 400;
    let w = img.width, h = img.height;
    if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
    else { w = Math.round(w * MAX / h); h = MAX; }

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, w, h);
    const compressed = canvas.toDataURL("image/jpeg", 0.8);
    const sizeKB = Math.round(compressed.length / 1024);
    console.log(`[imageCache] Compressed "${dishName}": ${Math.round(dataUrl.length/1024)}KB → ${sizeKB}KB`);

    // Use Edge Function instead of direct Supabase insert (Capacitor WebView blocks direct REST)
    // Wave L3 — when the write-time Vision gate said the image is bad,
    // we still store it but mark it flagged. source="flagged" tells the
    // edge function to set flagged=true; description carries the reason
    // for admin review.
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/store-dish-image`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${ANON_KEY}`,
      },
      body: JSON.stringify({
        dish_name: dishName,
        restaurant_name: restaurantName || null,
        description: writeAsFlagged
          ? `vision-gate flagged: ${verdictReason ?? "unspecified"}`
          : description || null,
        image_data: compressed,
        source: writeAsFlagged ? "flagged" : "generated",
      }),
    });
    if (!resp.ok) {
      const errText = await resp.text().catch(() => "unknown");
      console.error(`[imageCache] Edge fn store failed for "${dishName}": ${resp.status} ${errText}`);
    } else {
      console.log(`[imageCache] STORED IN DB: "${dishName}" (${sizeKB}KB)`);
    }
  } catch (err) {
    console.error(`[imageCache] compressAndStore failed for "${dishName}":`, fmtErr(err));
  }
}

/**
 * Wave F2 — category-based priority for the gen queue. Lower number = render
 * first. Mirrors the keyword buckets in CategoryPlaceholder.tsx but reduces
 * them to three priority bands. We deliberately avoid sharing state with
 * CategoryPlaceholder so a future tweak to the visual mapping doesn't
 * silently change generation order.
 */
const HIGH_PRI_RE = /\b(pizza|flatbread|focaccia|calzone|sushi|sashimi|nigiri|ramen|pho|udon|laksa|noodle|pasta|spaghetti|penne|rigatoni|linguine|carbonara|bolognese|lasagna|ravioli|gnocchi|risotto|burger|cheeseburger|smashed|patty|steak|ribeye|sirloin|tenderloin|filet|entrecote|wagyu|brisket|chicken|wings|tenders|katsu|schnitzel|parmigiana|salmon|tuna|cod|halibut|shrimp|prawn|lobster|crab|scallop|ceviche|tartare|crudo|taco|burrito|quesadilla|enchilada|fajita|nachos|salad|caesar|caprese|cobb|poke|bowl|soup|bisque|chowder|sandwich|sub|hoagie|panini|wrap|reuben|melt|dumpling|gyoza|pierogi|wonton|momo|empanada|bao|samosa|spring\s*roll|curry|masala|tikka|biryani|tagine|paella|jambalaya|chili|eggs|omelette|benedict|frittata|breakfast|pancake|french\s*toast)\b/i;
const LOW_PRI_RE = /\b(wine|champagne|prosecco|cava|sparkling|sauvignon|chardonnay|merlot|pinot|cabernet|malbec|riesling|beer|lager|ale|ipa|pilsner|stout|porter|cocktail|martini|negroni|margarita|mojito|spritz|aperol|whiskey|whisky|bourbon|gin|vodka|rum|tequila|mezcal|sangria|coffee|espresso|americano|cappuccino|latte|macchiato|mocha|cortado|tea|chai|matcha|juice|lemonade|smoothie|milkshake|shake|frappe|soda|cola|coke|sprite|fanta|tonic|seltzer|sparkling\s*water|drinks?|beverages?)\b/i;

export function dishGenPriority(name: string, description?: string): number {
  const haystack = `${name} ${description || ""}`.toLowerCase();
  if (HIGH_PRI_RE.test(haystack)) return 0; // mains first
  if (LOW_PRI_RE.test(haystack)) return 2;  // drinks last
  return 1;                                 // everything else in the middle
}

/**
 * Wave M1 — server-side batch image gen via the gen-dish-images-batch
 * edge function. ONE request for the whole menu; the server runs the
 * exact-match → LLM intent-map → vision-verify → generate pipeline at
 * 20-way concurrency (no iOS WKWebView socket cap) and streams NDJSON
 * back. Each line resolves one dish, so cards fill progressively.
 *
 * Returns { ok } so the caller can fall back to the legacy client-side
 * path (warmVisibleItems + queueImageGeneration) if the edge function is
 * unavailable or errors mid-stream. We cache every result locally
 * (mem + IDB + itemKey) exactly like the legacy path so re-renders and
 * the saved-menu view are instant.
 */
export async function warmVisibleItemsServerSide(
  items: Array<{ dishName: string; originalName?: string; description?: string; restaurantName?: string; itemKey?: string; index: number }>,
  onProgress?: (index: number, imageUrl: string | null, stage: "exact" | "mapped" | "generated" | "miss") => void,
): Promise<{ ok: boolean; resolvedIndices: Set<number> }> {
  const resolvedIndices = new Set<number>();
  if (!items.length) return { ok: true, resolvedIndices };
  if (!SUPABASE_URL) return { ok: false, resolvedIndices };

  // Map index → item so we can resolve itemKey/restaurant when caching.
  const byIndex = new Map<number, typeof items[0]>();
  for (const it of items) byIndex.set(it.index, it);

  try {
    const resp = await fetch(`${SUPABASE_URL}/functions/v1/gen-dish-images-batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${ANON_KEY}` },
      body: JSON.stringify({
        dishes: items.map((it) => ({
          name: it.dishName,
          description: it.description,
          restaurant_name: it.restaurantName,
          index: it.index,
        })),
      }),
    });
    if (!resp.ok || !resp.body) return { ok: false, resolvedIndices };

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let sawError = false;
    let resolvedAny = false;

    const handleLine = async (line: string) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      let obj: { index?: number; dish_name?: string; image_url?: string | null; stage?: string; error?: string };
      try {
        obj = JSON.parse(trimmed);
      } catch {
        return;
      }
      if (obj.error) { sawError = true; return; }
      if (typeof obj.index !== "number") return;
      const item = byIndex.get(obj.index);
      const url = obj.image_url || null;
      const stage = (obj.stage as "exact" | "mapped" | "generated" | "miss") || "miss";
      if (url && item) {
        // Cache locally so re-renders + saved-menu view are instant.
        const key = makeKey(item.dishName, item.restaurantName, item.description);
        memCache.set(key, url);
        await idbPut(STORE_NAME, key, url);
        if (item.itemKey) await cacheImageByItemKey(item.itemKey, url);
        if (stage === "exact" || stage === "mapped") trackStat("db_hits");
        else trackStat("generated");
        resolvedAny = true;
        resolvedIndices.add(obj.index);
      }
      onProgress?.(obj.index, url, stage);
    };

    // deno-lint-ignore no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nl: number;
      while ((nl = buffer.indexOf("\n")) >= 0) {
        const line = buffer.slice(0, nl);
        buffer = buffer.slice(nl + 1);
        await handleLine(line);
      }
    }
    if (buffer.trim()) await handleLine(buffer);

    // If the server errored before resolving anything, fall back.
    if (sawError && !resolvedAny) return { ok: false, resolvedIndices };
    return { ok: true, resolvedIndices };
  } catch (e) {
    console.warn("[imageCache] server-side batch gen failed, falling back:", fmtErr(e));
    return { ok: false, resolvedIndices };
  }
}

export async function warmVisibleItems(
  items: Array<{ dishName: string; originalName?: string; description?: string; restaurantName?: string; itemKey?: string; index: number }>,
  onProgress?: (index: number, imageUrl: string | null, stage: "local" | "db" | "generated" | "miss") => void,
): Promise<{ flaggedSlugs: Set<string> }> {
  if (!items.length) return { flaggedSlugs: new Set() };

  // ── Wave L3 — DB check via slim get-dish-images v2 ─────────────────
  //
  //   - restaurant_name passed in so the server filters by restaurant
  //     first (Wave L3 fix A) — kills the cross-restaurant pollution
  //     that was returning Denny's "shrimp" for a Bullfish scan.
  //   - Server returns either the cheap Storage URL (~120 chars) or
  //     the legacy inline base64 — both look like opaque `image_url`
  //     strings to us. After backfill completes, 100% are Storage URLs.
  //   - Server runs trigram fuzzy on misses (Wave L3 fix D), so OCR
  //     drift between scans of the same menu still hits the cache.
  //   - Timeout raised 10s → 25s. Now that the response is small (~5KB
  //     post-backfill instead of ~2MB), 25s is generous enough that we
  //     never race it on a normal connection; it's a safety net rather
  //     than a hot deadline.
  const dbImageMap = new Map<string, string>();
  const dbFlaggedSlugs = new Set<string>();
  // Pick the most common restaurantName from items (they should all share
  // one for a single menu scan; default to undefined if mixed/missing).
  const restaurantName = items.find((i) => i.restaurantName)?.restaurantName;
  try {
    const allNames = items.flatMap((i) => {
      const names = [i.dishName];
      if (i.originalName && i.originalName !== i.dishName) names.push(i.originalName);
      return names;
    });
    const dbCheck = fetch(`${SUPABASE_URL}/functions/v1/get-dish-images`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${ANON_KEY}` },
      body: JSON.stringify({
        dish_names: allNames,
        restaurant_name: restaurantName,
      }),
    }).then((r) => r.json());
    const timeout = new Promise<null>((r) => setTimeout(() => r(null), 25000));
    const result = await Promise.race([dbCheck, timeout]);
    const imageRows: Array<{ dish_name: string; image_url: string }> =
      result?.images || (Array.isArray(result) ? result : []);
    const flaggedRows: string[] = result?.flagged || [];
    const fuzzyHits: Array<{ requested: string; matched: string; score: number }> =
      result?.fuzzy || [];
    for (const row of imageRows) {
      if (row.dish_name && row.image_url) {
        if (!dbImageMap.has(row.dish_name)) dbImageMap.set(row.dish_name, row.image_url);
      }
    }
    for (const slug of flaggedRows) {
      dbFlaggedSlugs.add(slug);
    }
    // Wave L3 — log fuzzy hits separately so we can verify the server-side
    // fuzzy is doing real work (and not just blindly accepting weak matches).
    if (fuzzyHits.length > 0) {
      console.log(
        `[imageCache] DB check: ${dbImageMap.size} images (${fuzzyHits.length} via fuzzy), ${dbFlaggedSlugs.size} flagged`,
      );
      for (const h of fuzzyHits) {
        console.log(`  fuzzy: "${h.requested}" → "${h.matched}" (${h.score})`);
      }
    } else {
      console.log(`[imageCache] DB check: ${dbImageMap.size} images, ${dbFlaggedSlugs.size} flagged`);
    }
  } catch (e) {
    console.warn("[imageCache] DB check failed:", fmtErr(e));
  }

  // Process items with platform-tuned concurrency (Wave B.1):
  // iOS WKWebView ~6-connection cap → use 4 on native to avoid blocking
  // unrelated edge-function calls; 8 on web.
  const WARMUP_CONCURRENT = IMAGE_GEN_CONCURRENCY;
  let running = 0;
  // Wave L2: viewport-first ordering. Wave F2 sorted everything by
  // category priority (mains > sides > drinks) which felt fine for short
  // menus but broke on dense ones — the user's top-visible dishes ended
  // up scheduled AFTER mains lower on the menu, so the first 4 generation
  // slots were busy with things off-screen.
  //
  // New rule: the first FAST_LANE items (by source index, i.e. OCR order
  // = what appears top-of-menu) get priority over everything else. Within
  // that lane, source order is preserved so the very top dishes render
  // first. Wave F2's category priority still applies to items beyond the
  // fast lane (so once above-fold is done, mains > drinks for the rest).
  //
  // FAST_LANE = 8 fits a typical mobile above-the-fold grid (2 cols × 4
  // rows ≈ first scroll). Stable sort preserves relative order on ties.
  const FAST_LANE = 8;
  const pending = [...items].sort((a, b) => {
    const aTop = a.index < FAST_LANE;
    const bTop = b.index < FAST_LANE;
    if (aTop !== bTop) return aTop ? -1 : 1;
    if (aTop) return a.index - b.index; // strict OCR order in the fast lane
    return (
      dishGenPriority(a.dishName, a.description) -
      dishGenPriority(b.dishName, b.description)
    );
  });
  
  await new Promise<void>((resolveAll) => {
    let completed = 0;
    const total = items.length;
    
    const processNext = async () => {
      if (pending.length === 0) {
        if (completed >= total) resolveAll();
        return;
      }
      const item = pending.shift()!;
      running++;
      try {
        await processItem(item);
      } catch {}
      running--;
      completed++;
      if (completed >= total) resolveAll();
      else processNext();
    };
    
    // Start initial batch
    for (let i = 0; i < Math.min(WARMUP_CONCURRENT, pending.length); i++) {
      processNext();
    }
  });

  /** Background: if DB has a different (newer/golden) image, silently update local cache for next load */
  async function upgradeFromDB(
    item: typeof items[0],
    dbMap: Map<string, string>,
  ) {
    try {
      const canonical = canonicalDishName(item.dishName).replace(/\s+/g, "-");
      const dbHit = dbMap.get(canonical) || dbMap.get(item.dishName.toLowerCase());
      if (!dbHit) return;
      // Silently update local caches with DB version (will be used on next load)
      const key = makeKey(item.dishName, item.restaurantName, item.description);
      memCache.set(key, dbHit);
      await idbPut(STORE_NAME, key, dbHit);
      if (item.itemKey) await cacheImageByItemKey(item.itemKey, dbHit);
      console.log(`[imageCache] UPGRADED from DB: "${item.dishName}"`);
    } catch (e) {
      // Silent — upgrade is best-effort
    }
  }

  async function processItem(item: typeof items[0]) {
    try {
      // 1. Check local cache FIRST (instant, best UX)
      if (item.itemKey) {
        const byItem = await getCachedImageByItemKey(item.itemKey);
        if (byItem) {
          trackStat("prefetched");
          onProgress?.(item.index, byItem, "local");
          // Background: check if DB has a newer/better image and silently update local
          upgradeFromDB(item, dbImageMap);
          return;
        }
      }

      const local = await getCachedImageLocal(item.dishName, item.restaurantName);
      if (local) {
        if (item.itemKey) await cacheImageByItemKey(item.itemKey, local);
        trackStat("prefetched");
        onProgress?.(item.index, local, "local");
        // Background: check if DB has a newer/better image and silently update local
        upgradeFromDB(item, dbImageMap);
        return;
      }

      // 2. Check Supabase dish_images DB (no local cache exists)
      const canonical = canonicalDishName(item.dishName).replace(/\s+/g, "-");
      let dbHit = dbImageMap.get(canonical) || dbImageMap.get(item.dishName.toLowerCase());
      let fuzzyMatchedSlug: string | null = null;
      if (dbHit) {
        console.log(`[imageCache] DB HIT: "${item.dishName}" (canonical=${canonical})`);
      } else {
        // Wave E3: fuzzy fallback against the server-side cache before paying for
        // a gen. Uses the same token-similarity scoring as the local IndexedDB
        // fuzzy lookup, just applied to dbImageMap (whose entries are now
        // higher-quality after the Wave E1 audit flagged 70 bad images).
        // 0.7 threshold is slightly stricter than the local 0.66 because the
        // server pool is bigger so false-positive risk is higher.
        let bestSlug: string | null = null;
        let bestScore = 0;
        for (const slug of dbImageMap.keys()) {
          // Skip exact matches we already tried, and very short slugs (under
          // 3 tokens) which produce noisy 1.0 scores on single-word dishes.
          if (slug === canonical || slug === item.dishName.toLowerCase()) continue;
          const score = similarity(item.dishName, slug);
          if (score > bestScore) { bestScore = score; bestSlug = slug; }
        }
        if (bestSlug && bestScore >= 0.7) {
          dbHit = dbImageMap.get(bestSlug);
          fuzzyMatchedSlug = bestSlug;
          console.log(`[imageCache] DB FUZZY HIT: "${item.dishName}" → "${bestSlug}" (score=${bestScore.toFixed(2)})`);
        }
      }
      if (dbHit) {
        const key = makeKey(item.dishName, item.restaurantName, item.description);
        memCache.set(key, dbHit);
        await idbPut(STORE_NAME, key, dbHit);
        if (item.itemKey) await cacheImageByItemKey(item.itemKey, dbHit);
        trackStat("db_hits");
        onProgress?.(item.index, dbHit, "db");
        return;
      }
      void fuzzyMatchedSlug; // reserved for future analytics

      // 3. Generate → render immediately → compress & save to DB in background
      console.log(`[imageCache] MISS: "${item.dishName}" — generating...`);
      const { generateFoodImageDirect } = await import("@/lib/openrouterImage");
      const result = await generateFoodImageDirect(item.dishName, item.description);
      const url = result.imageUrl || null;
      if (url) {
        console.log(`[imageCache] GENERATED: "${item.dishName}" (${url.substring(0, 30)}...)`);
        // Cache locally + render immediately
        const rawKey = makeKey(item.dishName, item.restaurantName);
        memCache.set(rawKey, url);
        await idbPut(STORE_NAME, rawKey, url);
        if (item.itemKey) await cacheImageByItemKey(item.itemKey, url);
        trackStat("generated");
        onProgress?.(item.index, url, "generated");

        // Compress & save to DB in background (non-blocking)
        compressAndStoreInDB(item.dishName, item.restaurantName, item.description, url).catch((e) => console.error(`[imageCache] compressAndStore outer error:`, fmtErr(e)));
        return;
      }

      onProgress?.(item.index, null, "miss");
    } catch (err) {
      console.error(`[imageCache] ERROR for "${item.dishName}":`, fmtErr(err));
      onProgress?.(item.index, null, "miss");
    }
  }

  return { flaggedSlugs: dbFlaggedSlugs };
}

// ── Menu Fingerprint System ───────────────────────────────────────
const FINGERPRINT_KEY = "yumz_menu_fingerprints";

export function computeMenuFingerprint(restaurantName: string, items: { name: string }[]): string {
  const sortedNames = items.map((i) => (i.name || "").toLowerCase().trim()).sort().join("|");
  const str = `${(restaurantName || "").toLowerCase().trim()}::${sortedNames}`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

export function checkMenuFingerprint(fingerprint: string): boolean {
  try {
    const fps: Record<string, { savedAt: number }> = JSON.parse(localStorage.getItem(FINGERPRINT_KEY) || "{}");
    return !!fps[fingerprint];
  } catch {
    return false;
  }
}

export function storeMenuFingerprint(fingerprint: string): void {
  try {
    const fps: Record<string, { savedAt: number }> = JSON.parse(localStorage.getItem(FINGERPRINT_KEY) || "{}");
    fps[fingerprint] = { savedAt: Date.now() };
    // Keep only last 50 fingerprints to avoid localStorage bloat
    const keys = Object.keys(fps);
    if (keys.length > 50) {
      const oldest = keys.sort((a, b) => (fps[a].savedAt || 0) - (fps[b].savedAt || 0))[0];
      delete fps[oldest];
    }
    localStorage.setItem(FINGERPRINT_KEY, JSON.stringify(fps));
  } catch {
    // localStorage unavailable or full — ignore
  }
}

// ── Background generation queue ──────────────────────────────────
interface QueueItem {
  dishName: string;
  description?: string;
  restaurantName?: string;
  itemKey?: string;
  index: number;
  priority?: number;
  force?: boolean;
  onComplete: (index: number, imageUrl: string | null) => void;
}

const genQueue: QueueItem[] = [];
let genRunning = 0;
// Wave B.1: platform-tuned concurrency (see IMAGE_GEN_CONCURRENCY at top of file)
const MAX_CONCURRENT = IMAGE_GEN_CONCURRENCY;

let lastDrainActivity = Date.now();
const queuedKeys = new Set<string>();

function queueKey(dishName: string, _restaurantName?: string): string {
  return makeKey(dishName);
}

export function queueImageGeneration(
  dishName: string,
  description: string | undefined,
  restaurantName: string | undefined,
  index: number,
  onComplete: (index: number, imageUrl: string | null) => void,
  priority = 0,
  itemKey?: string,
  force = false,
) {
  const key = queueKey(dishName, restaurantName);
  if (!force && queuedKeys.has(key)) return;
  queuedKeys.add(key);

  const item: QueueItem = { dishName, description, restaurantName, itemKey, index, priority, force, onComplete };

  // Prioritize above-the-fold dishes so first cards fill fastest.
  if (priority > 0) {
    const insertAt = genQueue.findIndex((q) => (q.priority || 0) < priority);
    if (insertAt === -1) genQueue.push(item);
    else genQueue.splice(insertAt, 0, item);
  } else {
    genQueue.push(item);
  }

  drainQueue();

  // Watchdog: if queue stalls for 30s, force-reset and retry
  setTimeout(() => {
    if (genRunning > 0 && Date.now() - lastDrainActivity > 25000) {
      console.warn("[imageCache] WATCHDOG: queue stalled, resetting genRunning");
      genRunning = 0;
      drainQueue();
    }
  }, 30000);
}

// ── Wave L4-B — lazy image-gen registry ──────────────────────────
//
// Problem L4-B fixes: on a 50-item menu the previous flow called
// `queueImageGeneration` for ALL non-visible items immediately after
// the scan completed. That filled the generation queue (MAX_CONCURRENT
// = 4 on iOS) with 40+ items the user might never see, while the
// items they DID scroll to had to wait behind everything queued
// before them. End result: scrolling felt unresponsive and a full
// pass through a long menu took 4+ minutes.
//
// New flow: items beyond the initial fast lane are REGISTERED in
// `lazyGenRegistry`. They don't enter the generation queue until the
// IntersectionObserver in the results view fires
// `triggerLazyImageGen(restaurantName, dishName, index)` — which
// happens when the card scrolls within `rootMargin: 200px` of the
// viewport. So the user effectively pays only for what they look at,
// in the order they look at it.
//
// Keyed by `restaurantSlug::dishSlug::index` so duplicate dish names
// in different positions register separately, and the same dish in a
// different restaurant doesn't collide.

interface LazyGenEntry {
  dishName: string;
  description: string | undefined;
  restaurantName: string | undefined;
  itemKey: string | undefined;
  index: number;
  priority: number;
  onComplete: (index: number, imageUrl: string | null) => void;
}

const lazyGenRegistry = new Map<string, LazyGenEntry>();

function lazyGenKey(
  restaurantName: string | undefined,
  dishName: string,
  index: number,
): string {
  const r = canonicalRestaurantSlug(restaurantName);
  const d = canonicalDishName(dishName).replace(/\s+/g, "-");
  return `${r}::${d}::${index}`;
}

export function registerLazyImageGen(
  dishName: string,
  description: string | undefined,
  restaurantName: string | undefined,
  index: number,
  onComplete: (index: number, imageUrl: string | null) => void,
  priority = 0,
  itemKey?: string,
): void {
  const key = lazyGenKey(restaurantName, dishName, index);
  lazyGenRegistry.set(key, {
    dishName,
    description,
    restaurantName,
    itemKey,
    index,
    priority,
    onComplete,
  });
}

/**
 * Called by the IntersectionObserver in the results view when a card
 * scrolls into (or near) the viewport. Returns true if there was an
 * entry to fire, false otherwise. Idempotent: once an entry is
 * popped from the registry it's never re-queued by a later observer
 * fire, even if the card scrolls out and back in.
 */
export function triggerLazyImageGen(
  restaurantName: string | undefined,
  dishName: string,
  index: number,
): boolean {
  const key = lazyGenKey(restaurantName, dishName, index);
  const entry = lazyGenRegistry.get(key);
  if (!entry) return false;
  lazyGenRegistry.delete(key);
  queueImageGeneration(
    entry.dishName,
    entry.description,
    entry.restaurantName,
    entry.index,
    entry.onComplete,
    entry.priority,
    entry.itemKey,
  );
  return true;
}

/**
 * Clear the registry — call when navigating away from a scan or when
 * starting a new scan. Otherwise stale entries pile up across sessions.
 */
export function clearLazyImageGenRegistry(): void {
  lazyGenRegistry.clear();
}

/**
 * Drain ALL remaining lazy entries into the generation queue.
 * Used by Index.tsx after a fallback timeout (60s) so any items the
 * user never scrolled to still eventually generate — keeps the saved
 * menu state complete even if the user only viewed half the dishes.
 */
export function drainLazyImageGenRegistry(): number {
  const count = lazyGenRegistry.size;
  for (const [key, entry] of lazyGenRegistry) {
    lazyGenRegistry.delete(key);
    queueImageGeneration(
      entry.dishName,
      entry.description,
      entry.restaurantName,
      entry.index,
      entry.onComplete,
      Math.max(0, entry.priority - 10), // background priority
      entry.itemKey,
    );
  }
  return count;
}

async function drainQueue() {
  while (genQueue.length > 0 && genRunning < MAX_CONCURRENT) {
    const item = genQueue.shift()!;
    genRunning++;
    lastDrainActivity = Date.now();
    (async () => {
      try {
        // Skip cache checks when force-regenerating (user flagged wrong image)
        if (!item.force) {
          // Check item-key cache first, then dish-name cache.
          if (item.itemKey) {
            const itemCached = await getCachedImageByItemKey(item.itemKey);
            if (itemCached) {
              console.log(`[imageCache] ITEM KEY HIT: "${item.dishName}"`);
              item.onComplete(item.index, itemCached);
              return;
            }
          }

          const localCached = await getCachedImageLocal(item.dishName, item.restaurantName);
          if (localCached) {
            console.log(`[imageCache] LOCAL HIT: "${item.dishName}"`);
            item.onComplete(item.index, localCached);
            return;
          }

          // Skip per-item Supabase check — warmup already did batch DB check
          // Individual calls were causing connection exhaustion on iOS
        } // end !force

        console.log(`[imageCache] MISS: "${item.dishName}" — generating...`);
        // Generate
        const { generateFoodImageDirect } = await import("@/lib/openrouterImage");
        const result = await generateFoodImageDirect(item.dishName, item.description);
        const url = result.imageUrl || null;

        if (url) {
          console.log(`[imageCache] GENERATED: "${item.dishName}" (${url.slice(0, 30)}...)`);
          // Return image immediately
          item.onComplete(item.index, url);
          // Cache raw URL in memory + IndexedDB immediately (fast, no compression)
          const rawKey = makeKey(item.dishName, item.restaurantName);
          const genericKey = makeKey(item.dishName);
          const exactDisplayKey = normalizeKey(item.dishName);
          memCache.set(rawKey, url);
          memCache.set(genericKey, url);
          memCache.set(exactDisplayKey, url);
          if (item.itemKey) memCache.set(`item:${item.itemKey}`, url);
          try { await idbPut(STORE_NAME, rawKey, url); } catch {}
          try { await idbPut(STORE_NAME, genericKey, url); } catch {}
          try { await idbPut(STORE_NAME, exactDisplayKey, url); } catch {}
          if (item.itemKey) try { await idbPut(STORE_NAME, `item:${item.itemKey}`, url); } catch {}
          // Store in DB via Edge Function (non-blocking)
          compressAndStoreInDB(item.dishName, item.restaurantName, item.description, url).catch(() => {});
          return;
        } else {
          console.warn(`[imageCache] FAIL: "${item.dishName}"`, result.error);
        }
        item.onComplete(item.index, url);
      } catch {
        item.onComplete(item.index, null);
      } finally {
        queuedKeys.delete(queueKey(item.dishName, item.restaurantName));
        genRunning--;
        lastDrainActivity = Date.now();
        drainQueue();
      }
    })();
  }
}
